import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../config/axios";
import { errorHandler, successHandler } from "../../../helpers/handlers";
import { deleteDocument, uploadDocument } from "../../../config/firebase";

const initialState = {
  templates: [],
  selectedTemplate: null,
  pageLoading: false,
  actionLoading: false,
  error: null,
};

const slice = createSlice({
  name: "templates",
  initialState,
  reducers: {
    resetSelectedTemplate: (state) => {
      state.selectedTemplate = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all templates
      .addCase(fetchTemplates.pending, (state) => {
        state.pageLoading = true;
        state.error = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.pageLoading = false;
        state.templates = action.payload;
        state.error = null;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.pageLoading = false;
        state.error = action.payload;
      })

      // Fetch a single template
      .addCase(fetchTemplateById.pending, (state) => {
        state.pageLoading = true;
        state.error = null;
      })
      .addCase(fetchTemplateById.fulfilled, (state, action) => {
        state.pageLoading = false;
        state.selectedTemplate = action.payload;
        state.error = null;
      })
      .addCase(fetchTemplateById.rejected, (state, action) => {
        state.pageLoading = false;
        state.error = action.payload;
      })

      // Create a template
      .addCase(createTemplate.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.templates.unshift(action.payload); // Add the new template to the list
        state.error = null;
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })

      // Update a template
      .addCase(updateTemplate.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(updateTemplate.fulfilled, (state, action) => {
        state.actionLoading = false;
        const index = state.templates.findIndex(
          (template) => template.id === action.payload.id
        );
        if (index !== -1) {
          state.templates[index] = action.payload;
        }
        state.selectedTemplate = {
          ...state.selectedTemplate,
          ...action.payload,
        };
        state.error = null;
      })
      .addCase(updateTemplate.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })

      // Delete a template
      .addCase(deleteTemplate.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.templates = state.templates.filter(
          (template) => template.id !== action.payload.id
        );
        state.error = null;
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      });
  },
});

// Fetch all templates
export const fetchTemplates = createAsyncThunk(
  "templates/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/templates");
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

// Fetch a single template by ID
export const fetchTemplateById = createAsyncThunk(
  "templates/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/templates/${id}`);
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

// Create a new template
export const createTemplate = createAsyncThunk(
  "templates/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("/admin/templates", data);
      successHandler(response.data.message);
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

// Update an existing template
export const updateTemplate = createAsyncThunk(
  "templates/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      console.log("Updating template with data:", data);
      const oldAttachmentsToDelete = [];

      const tasksWithUploadedAttachments = await Promise.all(
        data.tasks.map(async (task) => {
          if (task.attachment && typeof task.attachment !== "string") {
            oldAttachmentsToDelete.push(task.attachment);
            const uploadedUrl = await uploadDocument(
              task.attachment,
              "templates",
              "doc"
            );
            return {
              ...task,
              attachment: uploadedUrl,
            };
          }
          return task;
        })
      );

      const response = await api.put(`/admin/templates/${id}`, {
        ...data,
        tasks: tasksWithUploadedAttachments,
      });

      successHandler(response.data.message);

      // Delete old attachments
      for (const oldAttachment of oldAttachmentsToDelete) {
        deleteDocument(oldAttachment);
      }

      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

// Delete a template
export const deleteTemplate = createAsyncThunk(
  "templates/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/admin/templates/${id}`);
      successHandler(response.data.message);
      return { id };
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const { resetSelectedTemplate } = slice.actions;

export default slice.reducer;
